import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Clock, X } from "lucide-react";
import React from "react";

export enum ExpiryStatus {
    SAFE = "Safe",
    EXPIRING_SOON = "Expiring Soon",
    EXPIRED = "Expired"
}

export interface ExpiryItemData {
    name: string;
    batch_no: string;
    quantity: number;
    location: string;
    expiry_date: string;
    status: ExpiryStatus;
    days_until_expiry: number;
}

interface ExpiryItemCardProps extends ExpiryItemData {}

const ExpiryMedicationCard: React.FC<ExpiryItemCardProps> = ({
    name,
    batch_no,
    quantity,
    expiry_date,
    status,
    location,
    days_until_expiry
}) => {

    const getCardBackgroundColor = () => {
        switch (status) {
            case ExpiryStatus.SAFE:
                return "bg-white";
            case ExpiryStatus.EXPIRING_SOON:
                return "bg-yellow-50 border-yellow-200";
            case ExpiryStatus.EXPIRED:
                return "bg-red-50 border-red-200";
            default:
                return "bg-white";
        }
    };

    const getStatusBadgeClass = () => {
        switch (status) {
            case ExpiryStatus.SAFE:
                return "bg-green-100 text-green-600 font-medium";
            case ExpiryStatus.EXPIRING_SOON:
                return "bg-yellow-100 text-yellow-700 font-medium";
            case ExpiryStatus.EXPIRED:
                return "bg-red-100 text-red-600 font-medium";
            default:
                return "bg-green-100 text-green-600 font-medium";
        }
    };

    const getDaysText = () => {
        if (status === ExpiryStatus.EXPIRED) {
            return `Expired ${Math.abs(days_until_expiry)} days ago`;
        } else if (status === ExpiryStatus.EXPIRING_SOON) {
            return `Expires in ${days_until_expiry} days`;
        }
        return `Safe - ${days_until_expiry} days remaining`;
    };

    const getTextColor = () => {
        switch (status) {
            case ExpiryStatus.EXPIRED:
                return "text-red-700";
            case ExpiryStatus.EXPIRING_SOON:
                return "text-yellow-700";
            default:
                return "text-gray-700";
        }
    };

    const getStatusTextWithIcon = () => {
        switch (status) {
            case ExpiryStatus.SAFE:
                return <div className="flex flex-row items-center justify-center gap-x-1">{getDaysText()}</div>
            case ExpiryStatus.EXPIRING_SOON:
                return <div className="flex flex-row items-center justify-center gap-x-1"> <Clock/>{getDaysText()}</div>
            case ExpiryStatus.EXPIRED:
                return <div className="flex flex-row items-center justify-center gap-x-1"> <AlertTriangle />{getDaysText()}</div>
        }
    }

    return (
        <Card className={`w-full border rounded-lg ${getCardBackgroundColor()}`}>
            <CardContent className="flex flex-row items-center justify-between p-4">
                <div className="flex flex-col items-start justify-center gap-y-1">
                    <span className={`font-medium text-lg ${getTextColor()}`}>{name}</span>
                    <span className="font-normal text-gray-400 text-sm">Batch: {batch_no}</span>
                    <span className="font-normal text-gray-400 text-xs">Qty: {quantity} units</span>
                </div>
                <div className="flex flex-col items-start justify-center gap-y-1">
                    <span className="text-xs text-gray-400">Location: {location}</span>
                    <span className="text-xs text-gray-400">Expires: {expiry_date}</span>
                </div>
                <div className="flex flex-col items-end justify-center gap-y-2">
                    <Badge className={getStatusBadgeClass()}>{getStatusTextWithIcon()}</Badge>
                    {(status === ExpiryStatus.EXPIRED || status === ExpiryStatus.EXPIRING_SOON) && (
                        <Button variant="outline" className="h-[30px] px-3 rounded-[8px] text-xs mt-1">
                            {status === ExpiryStatus.EXPIRED ? <div className="flex flex-row items-center justify-center gap-x-1"><X className="h-5 w-5"/><span>Dispose</span></div> : "Mark for Review"}
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default ExpiryMedicationCard;
